<!--
  Глобальный виджет быстрого доступа.
  Объединяет каналы поддержки, справочные материалы и вспомогательные инструменты.
  Отображается в правом нижнем углу интерфейса и может быть свернут пользователем.

  Есть два независимых источника "свёрнутого" вида:
  - isHiddenUntilReload — пользователь сам нажал крестик, сохраняется в
    localStorage и остаётся свёрнутым до тех пор, пока пользователь не
    нажмёт кнопку восстановления;
  - forceCollapsed — проп от страницы (через route.meta), которая хочет
    временно свернуть виджет, чтобы он не перекрывал её интерфейс
    (например страница Чатов). Это состояние не персистентное и не трогает
    isHiddenUntilReload: как только пользователь уходит со страницы,
    forceCollapsed снимается сам, и виджет возвращается в то состояние,
    в котором был до принудительного сворачивания.

  Клик по основной кнопке:
  - если forceCollapsed активен (мы на странице типа Чатов) — клик только
    открывает/закрывает меню и никогда не трогает persist-флаг
    isHiddenUntilReload, независимо от его значения;
  - иначе, если виджет скрыт самим пользователем — клик восстанавливает
    обычный круглый вид;
  - иначе — клик просто переключает меню.
-->
<template>
  <div
    ref="widget"
    class="support-widget"
    :class="{
      'support-widget_floating': floating,
      'support-widget_collapsed': isVisuallyCollapsed,
    }"
    @click.stop
  >
    <span
      v-if="tooltipText"
      class="support-widget__tooltip"
      role="tooltip"
    >
      {{ tooltipText }}
    </span>

    <transition name="support-widget-menu">
      <div
        v-if="isMenuOpen"
        class="support-widget__menu"
        role="menu"
        aria-label="Центр помощи"
        @click.stop
      >
        <div class="support-widget__menu-header">
          <span class="support-widget__menu-title">Центр помощи</span>
          <span class="support-widget__menu-description">
            Поддержка, соцсети и инструменты компании
          </span>
        </div>

        <div
          v-if="supportItems.length"
          class="support-widget__section"
        >
          <span class="support-widget__section-title">Поддержка</span>

          <div
            v-for="item in supportItems"
            :key="item.type"
            class="support-widget__menu-item"
            role="menuitem"
          >
            <a
              class="support-widget__menu-link"
              :href="item.href"
              :target="item.target"
              :rel="item.rel"
              @click="closeMenu"
            >
              <AppIcon :name="item.icon" size="17" />
              <span>{{ item.label }}</span>
              <AppIcon
                v-if="item.target === '_blank'"
                class="support-widget__external-icon"
                name="externalLink"
                size="14"
              />
            </a>

            <button
              v-if="item.copyValue"
              class="support-widget__copy"
              type="button"
              :aria-label="item.copyLabel"
              :title="item.copyLabel"
              @click.stop="copyToClipboard(item.copyValue)"
            >
              <AppIcon name="copy" size="14" />
            </button>
          </div>
        </div>

        <div
          v-if="socialItems.length"
          class="support-widget__section"
        >
          <span class="support-widget__section-title">
            Мы в соцсетях
          </span>

          <a
            v-for="item in socialItems"
            :key="item.type"
            class="support-widget__menu-link"
            :href="item.href"
            :target="item.target"
            :rel="item.rel"
            @click="closeMenu"
          >
            <AppIcon :name="item.icon" size="17" />
            <span>{{ item.label }}</span>
            <AppIcon
              class="support-widget__external-icon"
              name="externalLink"
              size="14"
            />
          </a>
        </div>

        <div class="support-widget__section">
          <span class="support-widget__section-title">Справка</span>

          <button
            class="support-widget__menu-link support-widget__menu-button"
            type="button"
            role="menuitem"
            @click="handleComingSoon('Документация')"
          >
            <AppIcon name="star" size="17" />
            <span>Документация</span>
          </button>

          <button
            class="support-widget__menu-link support-widget__menu-button"
            type="button"
            role="menuitem"
            @click="handleComingSoon('Что нового')"
          >
            <AppIcon name="clipboardCheck" size="17" />
            <span>Что нового</span>
          </button>
        </div>

        <!-- <div class="support-widget__section section-mobile">
          <span class="support-widget__section-title">
            Инструменты
          </span>

          <button
            v-for="item in toolsItems"
            :key="item.type"
            class="support-widget__menu-link support-widget__menu-button"
            type="button"
            role="menuitem"
            @click="handleComingSoon(item.label)"
          >
            <AppIcon :name="item.icon" size="17" />
            <span>{{ item.label }}</span>
          </button>
        </div> -->

        <div class="support-widget__section section-mobile">
          <span class="support-widget__section-title">
            Масштаб интерфейса
          </span>

          <div class="support-widget__scale-hint">
            <div class="support-widget__scale-row">
              <span class="support-widget__scale-label">
                Увеличить
              </span>
              <span class="support-widget__scale-keys">
                <kbd>{{ modifierKeyLabel }}</kbd>
                <kbd>+</kbd>
              </span>
            </div>

            <div class="support-widget__scale-row">
              <span class="support-widget__scale-label">
                Уменьшить
              </span>
              <span class="support-widget__scale-keys">
                <kbd>{{ modifierKeyLabel }}</kbd>
                <kbd>−</kbd>
              </span>
            </div>

            <div class="support-widget__scale-row">
              <span class="support-widget__scale-label">
                Сбросить
              </span>
              <span class="support-widget__scale-keys">
                <kbd>{{ modifierKeyLabel }}</kbd>
                <kbd>0</kbd>
              </span>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <button
      class="support-widget__toggle"
      :class="{
        'support-widget__toggle_collapsed': isVisuallyCollapsed,
      }"
      type="button"
      :aria-label="toggleAriaLabel"
      :aria-expanded="isMenuOpen.toString()"
      aria-haspopup="menu"
      @click.stop="handleToggleClick"
    >
      <AppIcon
        :name="toggleIconName"
        :size="isVisuallyCollapsed ? 16 : 22"
      />
    </button>

    <button
      v-if="!isVisuallyCollapsed"
      class="support-widget__close"
      type="button"
      aria-label="Свернуть центр помощи"
      title="Свернуть"
      @click.stop="hideUntilReload"
    >
      <AppIcon name="close" size="11" />
    </button>
  </div>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";
import { getSupportMaxUrl } from "@/config/supportLinks";
import { useNotificationStore } from "@/stores/notificationStore";

// Ключ хранения решения пользователя "скрыть центр помощи".
// Вынесен константой, чтобы не дублировать строку в коде.
const HIDDEN_STORAGE_KEY = "supportWidgetHidden";

export default {
  name: "SupportWidget",

  components: {
    AppIcon,
  },

  props: {
    floating: {
      type: Boolean,
      default: true,
    },
    // Проп от страницы (через route.meta.forceCollapseSupportWidget в
    // App.vue): временно сворачивает виджет, не трогая персистентное
    // isHiddenUntilReload. Снимается сам при переходе на другой роут.
    forceCollapsed: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isMenuOpen: false,
      isHiddenUntilReload: false,
    };
  },

  computed: {
    notificationStore() {
      return useNotificationStore();
    },

    // Свёрнутый вид показывается, если пользователь сам скрыл виджет
    // навсегда, либо если текущая страница просит временно его свернуть.
    isVisuallyCollapsed() {
      return this.isHiddenUntilReload || this.forceCollapsed;
    },

    toggleIconName() {
      return this.isMenuOpen ? "close" : "helpCenter";
    },

    tooltipText() {
      if (this.isMenuOpen) return "";
      if (this.isVisuallyCollapsed) return "Показать центр помощи";

      return "Центр помощи";
    },

    maxBotUrl() {
      return getSupportMaxUrl();
    },

    supportEmail() {
      return (process.env.VUE_APP_SUPPORT_EMAIL || "").trim();
    },

    supportPhone() {
      return (process.env.VUE_APP_SUPPORT_PHONE || "").trim();
    },

    supportItems() {
      return [
        this.maxBotUrl && {
          type: "max",
          label: "Написать в MAX",
          icon: "max",
          href: this.maxBotUrl,
          target: "_blank",
          rel: "noopener noreferrer",
        },
        this.supportEmail && {
          type: "email",
          label: this.supportEmail,
          icon: "email",
          href: `mailto:${this.supportEmail}`,
          target: null,
          rel: null,
          copyValue: this.supportEmail,
          copyLabel: "Скопировать почту",
        },
        this.supportPhone && {
          type: "phone",
          label: this.supportPhone,
          icon: "phone",
          href: `tel:${this.phoneHref}`,
          target: null,
          rel: null,
          copyValue: this.supportPhone,
          copyLabel: "Скопировать телефон",
        },
      ].filter(Boolean);
    },

    socialItems() {
      return [
        {
          type: "telegram",
          label: "Telegram",
          icon: "send",
          href: "https://t.me/truckocrm",
          target: "_blank",
          rel: "noopener noreferrer",
        },
        {
          type: "max-community",
          label: "MAX",
          icon: "max",
          href: "https://max.ru/join/6ux6qRvnjAqbHdFmFOWaeZJVBmfXT1_i9p9AaBcSYng",
          target: "_blank",
          rel: "noopener noreferrer",
        },
      ];
    },

    toolsItems() {
      return [
        {
          type: "calculator",
          label: "Калькулятор",
          icon: "homeCalculator",
        },
        {
          type: "calendar",
          label: "Календарь",
          icon: "calendar",
        },
        {
          type: "currency",
          label: "Конвертер валют",
          icon: "sync",
        },
      ];
    },

    phoneHref() {
      return this.supportPhone.replace(/[^\d+]/g, "");
    },

    toggleAriaLabel() {
      if (this.isMenuOpen) return "Закрыть центр помощи";
      if (this.isVisuallyCollapsed) return "Показать центр помощи";

      return "Открыть центр помощи";
    },

    // На macOS масштаб страницы меняется через Cmd, на Windows/Linux — через Ctrl.
    isMacPlatform() {
      const platform =
        navigator.userAgentData?.platform || navigator.platform || "";

      return /mac/i.test(platform);
    },

    modifierKeyLabel() {
      return this.isMacPlatform ? "⌘" : "Ctrl";
    },
  },

  watch: {
    // Если страница принудительно сворачивает виджет, попутно открытое
    // меню логично спрятать, чтобы оно не "зависало" открытым при переходе.
    isVisuallyCollapsed(newValue) {
      if (newValue) this.isMenuOpen = false;
    },
  },

  created() {
    document.addEventListener("click", this.handleOutsideClick);
    document.addEventListener("keydown", this.handleKeydown);

    this.isHiddenUntilReload =
      localStorage.getItem(HIDDEN_STORAGE_KEY) === "true";
  },

  unmounted() {
    document.removeEventListener("click", this.handleOutsideClick);
    document.removeEventListener("keydown", this.handleKeydown);
  },

  methods: {
    // Поведение клика по основной кнопке:
    // - если текущая страница принудительно сворачивает виджет
    //   (forceCollapsed) — клик только переключает меню и никогда не
    //   трогает persist-флаг isHiddenUntilReload, даже если он включён;
    //   иначе первый клик на такой странице "тратился" бы на снятие
    //   persist-флага и требовал бы второго клика для открытия меню, а
    //   после ухода со страницы виджет остался бы видимым без разрешения
    //   пользователя;
    // - иначе, если виджет скрыт самим пользователем — клик восстанавливает
    //   обычный вид;
    // - иначе — обычный toggle меню.
    handleToggleClick() {
      if (this.forceCollapsed) {
        this.isMenuOpen = !this.isMenuOpen;
        return;
      }

      if (this.isHiddenUntilReload) {
        this.restoreWidget();
        return;
      }

      this.isMenuOpen = !this.isMenuOpen;
    },

    closeMenu() {
      this.isMenuOpen = false;
    },

    hideUntilReload() {
      this.isHiddenUntilReload = true;
      this.isMenuOpen = false;
      localStorage.setItem(HIDDEN_STORAGE_KEY, "true");

      this.blurActiveElement();
    },

    restoreWidget() {
      this.isHiddenUntilReload = false;
      localStorage.setItem(HIDDEN_STORAGE_KEY, "false");
    },

    blurActiveElement() {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    },

    handleOutsideClick() {
      if (!this.isMenuOpen) return;

      this.isMenuOpen = false;
    },

    handleKeydown(event) {
      if (event.key === "Escape") this.isMenuOpen = false;
    },

    handleComingSoon(title) {
      this.notificationStore.showSuccess(`${title}: скоро появится`, {
        timeout: 1800,
      });
    },

    async copyToClipboard(value) {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(value);
        this.showCopiedNotification();
        return;
      }

      this.copyToClipboardFallback(value);
      this.showCopiedNotification();
    },

    copyToClipboardFallback(value) {
      const element = document.createElement("textarea");
      element.value = value;
      element.setAttribute("readonly", "");
      element.style.position = "fixed";
      element.style.opacity = "0";
      document.body.appendChild(element);
      element.select();
      document.execCommand("copy");
      document.body.removeChild(element);
    },

    showCopiedNotification() {
      this.notificationStore.showSuccess("Скопировано", {
        timeout: 1800,
      });
    },
  },
};
</script>

<style scoped>
.support-widget {
  position: relative;
  width: var(--app-floating-action-size, 48px);
  height: var(--app-floating-action-size, 48px);
  z-index: 1800;
}

.support-widget_floating {
  position: fixed;
  right: 24px;
  bottom: 24px;
}

.support-widget_collapsed {
  width: 30px;
}

.support-widget_collapsed.support-widget_floating {
  right: 0;
}

.support-widget__toggle {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 0;
  color: #fff;
  background-color: var(--color-green);
  border: none;
  border-radius: 50%;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.16);
  opacity: 0.82;
  cursor: pointer;
  transition: background-color 0.2s ease, box-shadow 0.2s ease,
    opacity 0.2s ease, transform 0.2s ease, border-radius 0.2s ease,
    width 0.2s ease;
}

.support-widget__toggle_collapsed {
  width: 24px;
  height: 48px;
  color: rgba(255, 255, 255, 0.9);
  background-color: rgba(31, 41, 55, 0.42);
  border: 1px solid rgba(255, 255, 255, 0.42);
  border-right: none;
  border-radius: 9px 0 0 9px;
  box-shadow: -3px 4px 12px rgba(0, 0, 0, 0.14);
  opacity: 0.44;
  transform: translateX(39px);
}

.support-widget__toggle_collapsed:hover,
.support-widget__toggle_collapsed:focus-visible {
  color: #fff;
  background-color: var(--color-green);
  box-shadow: -4px 5px 14px rgba(0, 0, 0, 0.2);
  opacity: 1;
  transform: translateX(32px);
}

.support-widget:hover
  .support-widget__toggle:not(.support-widget__toggle_collapsed),
.support-widget__toggle:focus-visible:not(
    .support-widget__toggle_collapsed
  ) {
  background-color: var(--color-green-hover);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.2);
  opacity: 1;
  transform: translateY(-1px);
}

.support-widget__toggle:focus-visible {
  outline: 3px solid rgba(0, 191, 165, 0.28);
  outline-offset: 3px;
}

.support-widget__tooltip {
  position: absolute;
  top: 50%;
  right: calc(100% + 10px);
  padding: 7px 9px;
  color: #fff;
  background-color: #1f2937;
  border-radius: 6px;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transform: translateY(-50%) translateX(4px);
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.support-widget__tooltip::after {
  content: "";
  position: absolute;
  top: 50%;
  right: -5px;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 5px solid #1f2937;
  transform: translateY(-50%);
}

.support-widget:has(.support-widget__toggle:hover)
  .support-widget__tooltip,
.support-widget:has(.support-widget__toggle:focus-visible)
  .support-widget__tooltip {
  opacity: 1;
  transform: translateY(-50%);
}

.support-widget__menu {
  position: absolute;
  right: 0;
  bottom: calc(100% + 10px);
  z-index: 3;
  width: 286px;
  max-width: calc(100vw - 32px);
  overflow: hidden;
  background-color: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  border-bottom-right-radius: 0;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
}

.support-widget__menu-header {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 10px;
  padding: 14px 14px 13px;
  background: linear-gradient(
    135deg,
    rgba(0, 191, 165, 0.22),
    rgba(0, 191, 165, 0.08)
  );
  border-bottom: 1px solid #e5e7eb;
}

.support-widget__menu-title {
  color: var(--color-dark);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
}

.support-widget__menu-description {
  color: #6b7280;
  font-size: 13px;
  line-height: 1.25;
}

.support-widget__section {
  padding: 10px 6px;
}

.support-widget__section + .support-widget__section {
  border-top: 1px solid #edf0f2;
}

.support-widget__section-title {
  display: block;
  padding: 0 8px 7px;
  color: var(--color-green-hover);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
}

.support-widget__menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  width: 100%;
  border-radius: 7px;
}

.support-widget__menu-link {
  display: flex;
  align-items: center;
  gap: 9px;
  flex: 1;
  min-width: 0;
  min-height: 38px;
  padding: 9px 10px;
  color: #374151;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.1;
  text-align: left;
}

.support-widget__menu-link span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.support-widget__menu-link:hover,
.support-widget__menu-link:focus-visible {
  color: var(--color-green-hover);
  background-color: #f3f4f6;
  outline: none;
}

.support-widget__menu-button {
  width: 100%;
  background-color: transparent;
  border: none;
  cursor: pointer;
}

.support-widget__external-icon {
  flex: 0 0 auto;
  margin-left: auto;
  color: currentColor;
}

.support-widget__copy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  margin-right: 4px;
  padding: 0;
  color: #6b7280;
  background-color: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.support-widget__copy:hover,
.support-widget__copy:focus-visible {
  color: var(--color-green-hover);
  background-color: #f3f4f6;
  outline: none;
}

.support-widget__scale-hint {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px 8px 4px;
}

.support-widget__scale-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 2px;
}

.support-widget__scale-label {
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.1;
}

.support-widget__scale-keys {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
}

.support-widget__scale-keys kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  color: #4b5563;
  background-color: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}

.support-widget__close {
  position: absolute;
  top: -7px;
  right: -7px;
  z-index: 4;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 19px;
  height: 19px;
  padding: 0;
  color: #4b5563;
  background-color: #fff;
  border: 1px solid #d1d5db;
  border-radius: 50%;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.16);
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transition: background-color 0.2s ease, color 0.2s ease,
    opacity 0.2s ease;
}

.support-widget:hover .support-widget__close,
.support-widget:focus-within .support-widget__close {
  opacity: 1;
  pointer-events: auto;
}

.support-widget__close:hover,
.support-widget__close:focus-visible {
  color: #111827;
  background-color: #f3f4f6;
}

.support-widget__close:focus-visible {
  outline: 2px solid rgba(0, 191, 165, 0.35);
  outline-offset: 2px;
}

.support-widget-menu-enter-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.support-widget-menu-leave-active {
  transition: opacity 0.14s ease, transform 0.14s ease;
}

.support-widget-menu-enter-from,
.support-widget-menu-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

@media (max-width: 1024px) {
  .section-mobile {
    display: none;
  }
}

@media (max-width: 768px) {
  .support-widget_floating {
    right: 16px;
    bottom: 16px;
  }

  .support-widget_collapsed.support-widget_floating {
    right: 0;
  }

  .support-widget__toggle_collapsed {
    width: 24px;
    height: 48px;
    transform: translateX(34px);
  }

  .support-widget__toggle_collapsed:hover,
  .support-widget__toggle_collapsed:focus-visible {
    transform: translateX(28px);
  }

  .support-widget__menu {
    width: min(286px, calc(100vw - 32px));
  }
}
</style>
